import { FieldDictionary } from '@shopify/react-form';
import Row from '@sdk/components/ui/spacing/Row';
import gettext from '@sdk/lang/gettext';
import Button from '@sdk/components/ui/buttons/Button';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import TextField from '@sdk/components/forms/TextField';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { useCurrency } from 'sling-sdk/src/common/currency/CurrencyContext';
import { getCurrencySeparators } from 'sling-sdk/src/common/currency/helpers/getCurrencySeparators';

import type { Employee } from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import {
  Container,
  ModalContent,
  FooterRow,
  ButtonContainer,
  ScrollableArea,
} from '@web/onboarding/Checklist/components/modalBase.style';
import { disabledInputLookingLikeRegularCss } from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.styles';
import CurrencyField from '@web/onboarding/Checklist/components/CurrencyField';

export const SCROLLABLE_AREA_MAX_HEIGHT = 385;

type Props = {
  fields: FieldDictionary<Employee>[];
  submitWages: () => Promise<void>;
  onCancel: () => void;
};

const WagesForEmployeesContent = ({ fields, submitWages, onCancel }: Props) => {
  const { currency } = useCurrency();
  const { decimalSeparator, thousandsSeparator } = getCurrencySeparators(currency.decimalFormat);

  return (
    <Container noMinHeight>
      <ModalContent>
        <ScrollableArea pLeft={15} pRight={15} maxHeight={SCROLLABLE_AREA_MAX_HEIGHT}>
          {fields.map((field, index) => (
            <Row key={field.id.value} mTop={15}>
              <TextField
                name="fullName"
                value={field.fullName.value}
                placeholder={gettext('Employee')}
                label={index === 0 ? gettext('Employee') : null}
                height={INPUT_HEIGHT}
                disabledStyleOverride={disabledInputLookingLikeRegularCss}
                disabled
              />
              <ModalContent left={10}>
                <CurrencyField
                  currency={currency}
                  decimalSeparator={decimalSeparator}
                  thousandSeparator={thousandsSeparator}
                  name="wage"
                  value={field.wage.value}
                  placeholder={gettext('Hourly wage')}
                  label={index === 0 ? gettext('Hourly wage') : null}
                  onValueChange={(value) => field.wage.onChange(value.value)}
                  onBlur={field.wage.onBlur}
                  height={INPUT_HEIGHT}
                  invalid={Boolean(field.wage.error)}
                  error={field.wage.error}
                  isNumericString
                />
              </ModalContent>
            </Row>
          ))}
        </ScrollableArea>
      </ModalContent>
      <FooterRow mTop={40} pLeft={15} pRight={15}>
        <ButtonContainer pRight={10}>
          <Button buttonType={ButtonType.default} onClick={onCancel}>
            {gettext('Cancel')}
          </Button>
        </ButtonContainer>

        <ButtonContainer>
          <ProgressButton
            buttonType={ButtonType.primary}
            onClick={submitWages}
            height={INPUT_HEIGHT}>
            {gettext('Save')}
          </ProgressButton>
        </ButtonContainer>
      </FooterRow>
    </Container>
  );
};

export default WagesForEmployeesContent;
